import React from 'react';
import {MainHeader} from 'presentation/components';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {Typography, useTheme} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {useFavorites} from './hooks/useFavorites';
import {ALL_SPECIALTIES_KEY} from './hooks/useSpecialtyFilter';
import {FavoriteDoctorsLayout} from './components/FavoriteDoctorsLayout';
import {EmptyState} from './components/EmptyState';
import {EmptySearchState} from './components/EmptySearchState';
import {ErrorState} from './components/ErrorState';
import {LoadingFavorites} from './components/LoadingFavorites';
import {ProviderListView} from './components/ProviderListView';
import {SpecialtyListView} from './components/SpecialtyListView';
import {Filter} from 'presentation/components/Filter';
import BookingVerifications from './components/BookingVerifications';
import {formatText} from 'presentation/utils';
import {FavoriteDoctorsSpecialty} from 'domain/usecases';
import {Drawer} from 'presentation/components/Drawer';

const {favorites} = translations['pt-br'].privateFlow.services;
const {select, agreementText} = translations['pt-br'].commons;
const {
	allSpecialties,
	unaccreditedProviders,
	unaccreditedProvidersDescription,
	unaccreditedProvidersDescription2,
} = translations['pt-br'].privateFlow.guideFlow.favoriteDoctors;
const {searchProfessional} = translations['pt-br'].privateFlow.bookingFlow;

const FavoriteDoctors = () => {
	const {isMobile} = useMediaQueries();
	const {colors} = useTheme();
	const {
		isLoading,
		isError,
		refetch,
		specialtySelected,
		subSpecialtySelected,
		formatSpecialtyTitle,
		handleGoToHome,
		handleResetSpecialty,
		handleSpecialtyClick,
		doctors,
		query,
		handleScheduleAppointment,
		bookingTheme,
		swapSpecialtyCallback,
		checkBookingModal,
		setCheckBookingModal,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		selectedProfessional,
		handleResetProfessionalSelection,
		openUnaccreditedProvidersDrawer,
		handleOpenUnaccreditedProvidersDrawer,
		handleCloseUnaccreditedProvidersDrawer,
	} = useFavorites();

	if (isLoading) {
		return (
			<FavoriteDoctorsLayout title={favorites}>
				<MainHeader title={favorites} handleBack={handleGoToHome} hideProfileMenu />
				<LoadingFavorites isMobile={isMobile} />
			</FavoriteDoctorsLayout>
		);
	}

	if (isError) {
		return (
			<FavoriteDoctorsLayout title={favorites}>
				<MainHeader title={favorites} handleBack={handleGoToHome} hideProfileMenu />
				<ErrorState isMobile={isMobile} refetch={refetch} />
			</FavoriteDoctorsLayout>
		);
	}

	if (!doctors?.especialidades?.length) {
		return (
			<FavoriteDoctorsLayout title={favorites}>
				<MainHeader title={favorites} handleBack={handleGoToHome} hideProfileMenu />
				<EmptyState isMobile={isMobile} />
			</FavoriteDoctorsLayout>
		);
	}

	const getSpecialtyTitle = (specialty: string, subSpecialty?: string) =>
		subSpecialty
			? `${formatText(specialty)} - ${formatText(subSpecialty)}`
			: formatText(specialty);

	const getSpecialtyOrSpecialtySelected = (
		specialtySelected: string,
		specialty?: FavoriteDoctorsSpecialty,
	) =>
		specialty
			? getSpecialtyTitle(specialty.nmEspecialidade, specialty.nmSubEspecialidade)
			: formatText(specialtySelected);

	if (specialtySelected) {
		const isAllSpecialties = specialtySelected === ALL_SPECIALTIES_KEY;
		const specialty = doctors.especialidades.find(
			s =>
				s.nmEspecialidade === specialtySelected &&
				(!subSpecialtySelected || s.nmSubEspecialidade === subSpecialtySelected),
		);

		const title = isAllSpecialties
			? allSpecialties
			: getSpecialtyOrSpecialtySelected(specialtySelected, specialty);

		const allProfessionals = doctors.especialidades.flatMap(s => s.profissionais);
		const providers = (
			isAllSpecialties ? allProfessionals : (specialty?.profissionais ?? [])
		).filter(provider =>
			provider?.nmPrestadorFisico?.toLowerCase().includes(query?.trim().toLowerCase()),
		);

		return (
			<FavoriteDoctorsLayout title={favorites}>
				<MainHeader
					title={title}
					handleBack={handleResetSpecialty}
					handleClose={handleGoToHome}
					variant="subRoute"
					hideProfileMenu>
					<Filter.Root>
						<Filter.Search placeholder={searchProfessional} />
					</Filter.Root>
				</MainHeader>

				{providers.length === 0 && query ? (
					<EmptySearchState isMobile={isMobile} />
				) : (
					<ProviderListView
						providers={providers}
						iconName={bookingTheme.iconName}
						onScheduleAppointment={handleScheduleAppointment}
						handleOpenUnaccreditedProvidersDrawer={
							handleOpenUnaccreditedProvidersDrawer
						}
					/>
				)}
				<BookingVerifications
					checkBookingModal={checkBookingModal}
					setCheckBookingModal={setCheckBookingModal}
					loadingText={loadingText}
					resetBookingDataSpecialties={resetBookingDataSpecialties}
					handleChooseScriptSuggestion={handleChooseScriptSuggestion}
					scheduledBooking={scheduledBooking}
					swapSpecialtyCallback={swapSpecialtyCallback}
					selectedProfessional={selectedProfessional}
					handleResetProfessionalSelection={handleResetProfessionalSelection}
				/>
				<Drawer.Root
					open={openUnaccreditedProvidersDrawer}
					onClose={handleCloseUnaccreditedProvidersDrawer}
					onOpen={handleOpenUnaccreditedProvidersDrawer}
					customMaxWidth="600px">
					<Drawer.Header
						title={unaccreditedProviders}
						onClose={handleCloseUnaccreditedProvidersDrawer}
					/>
					<Drawer.Content gap={2}>
						<Typography>{unaccreditedProvidersDescription}</Typography>
						<Typography>{unaccreditedProvidersDescription2}</Typography>
					</Drawer.Content>
					<Drawer.Footer
						secondButtonTitle={agreementText}
						secondButtonOnClick={handleCloseUnaccreditedProvidersDrawer}
						hideFirstButton
					/>
				</Drawer.Root>
			</FavoriteDoctorsLayout>
		);
	}

	return (
		<FavoriteDoctorsLayout title={favorites}>
			<MainHeader title={favorites} handleBack={handleGoToHome} hideProfileMenu />
			<SpecialtyListView
				specialties={doctors.especialidades}
				totalFavorites={doctors.totalMedicosFavoritados}
				specialtySelected={specialtySelected}
				allSpecialtiesLabel={allSpecialties}
				selectLabel={select}
				isMobile={isMobile}
				colors={colors}
				formatSpecialtyTitle={formatSpecialtyTitle}
				onSpecialtyClick={handleSpecialtyClick}
			/>
		</FavoriteDoctorsLayout>
	);
};

export {FavoriteDoctors};
